import { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import apiClient from './client';

/**
 * API Interceptors Configuration
 * Handles authentication, error handling, and response transformation
 */

// Request Interceptor: Add JWT token to all requests
apiClient.interceptors.request.use(
  (config: InternalAxiosRequestConfig) => {
    const token = localStorage.getItem('token');

    if (token && config.headers) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error: AxiosError) => {
    return Promise.reject(error);
  }
);

// Response Interceptor: Handle success and errors
apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Extract data from the API wrapper format
    // API returns: { success: true, message: "...", data: {...}, errors: null }
    if (response.data && typeof response.data === 'object' && 'data' in response.data) {
      return {
        ...response,
        data: response.data.data,
      };
    }

    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized - Try to refresh token
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      try {
        const refreshToken = localStorage.getItem('refreshToken');

        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // Try to refresh the token
        const response = await apiClient.post('/auth/refresh', { refreshToken });
        const { token: newToken } = response.data;

        // Update stored token
        localStorage.setItem('token', newToken);

        // Retry the original request with new token
        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${newToken}`;
        }

        return apiClient(originalRequest);
      } catch (refreshError) {
        // Refresh failed - clear auth data and redirect to login
        try {
          localStorage.removeItem('token');
          localStorage.removeItem('refreshToken');
          localStorage.removeItem('user');

          toast.error('Sessão expirada. Por favor, faça login novamente.');

          // Redirect to login page after a small delay to show the toast
          setTimeout(() => {
            window.location.href = '/login';
          }, 1000);
        } catch (cleanupError) {
          console.error('Error during logout cleanup:', cleanupError);
          // Force redirect anyway
          window.location.href = '/login';
        }

        return Promise.reject(refreshError);
      }
    }

    // Handle other HTTP errors
    const errorMessage = getErrorMessage(error);
    const status = error.response?.status;

    // Reporta ao Sentry com o contexto da requisição (método/URL/status) para
    // permitir rastrear qual endpoint falhou. O 401 é tratado pelo fluxo de
    // refresh acima e o 404 é tratado como "não encontrado" pela UI.
    if (status !== 401 && status !== 404) {
      Sentry.captureException(error, {
        level: status && status >= 500 ? 'error' : 'warning',
        tags: {
          http_status: status ?? 'network_error',
          http_method: originalRequest?.method?.toUpperCase() ?? 'unknown',
        },
        extra: {
          method: originalRequest?.method?.toUpperCase(),
          url: originalRequest?.url,
          baseURL: originalRequest?.baseURL,
          params: originalRequest?.params,
          status,
          responseMessage: (error.response?.data as { message?: string } | undefined)?.message,
        },
      });
    }

    // Don't show toast for certain status codes (handled by components)
    const silentErrors = [404];
    if (!silentErrors.includes(status || 0)) {
      toast.error(errorMessage);
    }

    return Promise.reject(error);
  }
);

/**
 * Extract user-friendly error message from API error
 */
function getErrorMessage(error: AxiosError): string {
  if (error.response) {
    // Server responded with error status
    const data = error.response.data as any;

    if (data?.message) {
      return data.message;
    }

    if (data?.errors && Array.isArray(data.errors) && data.errors.length > 0) {
      return data.errors[0];
    }

    // Default messages by status code
    switch (error.response.status) {
      case 400:
        return 'Requisição inválida. Verifique os dados enviados.';
      case 401:
        return 'Não autorizado. Faça login novamente.';
      case 403:
        return 'Você não tem permissão para realizar esta ação.';
      case 404:
        return 'Recurso não encontrado.';
      case 422:
        return 'Dados inválidos. Verifique as informações e tente novamente.';
      case 500:
        return 'Erro interno do servidor. Tente novamente mais tarde.';
      default:
        return 'Erro ao processar requisição.';
    }
  } else if (error.request) {
    // Request was made but no response received
    return 'Erro de conexão. Verifique sua internet e tente novamente.';
  } else {
    // Something else happened
    return error.message || 'Erro desconhecido.';
  }
}

export default apiClient;
